import { useCallback, useState } from 'react'
import { EInvoiceQueryState } from './einvoice-query-state'
import type { EInvoiceResolutionSource } from '@/lib/api/einvoice-api'
import { EInvoicePanelState } from '@/components/sections/einvoice-panel-state'
import {
    usePublishEInvoice,
    useRefreshEInvoice,
    useRefreshEInvoiceDraft,
} from '@/hooks/use-einvoice-draft-mutations'
import { useCreateEInvoiceDraft, useEInvoice } from '@/hooks/use-einvoice-query'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

interface Props {
    orderId: string
    orderStatus: string
    /** Đơn "Bán ngay": chốt đơn là được phát hành (M-132). */
    isQuickSale?: boolean
    canExport: boolean
    customerId?: string
    orderCode?: string
    deliveredAt?: string
    /** Screen the order was opened from, for usage analytics. */
    source?: EInvoiceResolutionSource
}

export function EInvoicePanel({
    orderId,
    orderStatus,
    isQuickSale,
    canExport,
    orderCode,
    deliveredAt,
    source,
}: Props) {
    const enabled = useFeatureFlag('dev-einvoice')
    const query = useEInvoice(orderId, Boolean(enabled))
    const draft = useCreateEInvoiceDraft()
    const refresh = useRefreshEInvoice()
    const refreshDraft = useRefreshEInvoiceDraft()
    const publish = usePublishEInvoice()
    const [created, setCreated] = useState(false)
    // The preview opens once from "Phát hành"; later viewers start closed.
    const clearCreated = useCallback(() => setCreated(false), [])
    const refreshWorkflow = useWorkflowTracking('einvoice_refresh', {
        autoStart: false,
        attemptKey: orderId,
        operation: 'update',
    })
    const publishWorkflow = useWorkflowTracking('einvoice_push', {
        autoStart: false,
        attemptKey: orderId,
        operation: 'submit',
    })
    const refreshNow = () =>
        refreshWorkflow.run(
            () => refresh.mutateAsync(orderId),
            (data) => data?.id,
        )
    if (!enabled) return null
    if (query.isLoading || query.isError)
        return (
            <EInvoiceQueryState
                error={query.error}
                retry={() => void query.refetch()}
            />
        )
    return (
        <EInvoicePanelState
            key={orderId}
            orderId={orderId}
            orderCode={orderCode}
            orderStatus={orderStatus}
            isQuickSale={isQuickSale}
            deliveredAt={deliveredAt}
            source={source}
            invoice={query.data ?? null}
            canMutate={
                canExport && !['cancelled', 'returned'].includes(orderStatus)
            }
            pushing={draft.isPending}
            refreshing={refresh.isPending}
            initiallyOpen={created}
            onInitiallyOpened={clearCreated}
            onPush={() => {
                void draft
                    .mutateAsync(orderId)
                    .then(() => setCreated(true))
                    .catch(() => {})
            }}
            onRefresh={() => {
                void refreshNow().catch(() => {})
            }}
            onRefreshNow={refreshNow}
            onRefreshDraft={(buyer, content, reset) =>
                refreshDraft.mutateAsync({
                    orderId,
                    buyer,
                    draft: content,
                    reset,
                })
            }
            onPublish={(previewToken, notIssuedByHand) =>
                publishWorkflow.run(
                    () =>
                        publish.mutateAsync({
                            orderId,
                            previewToken,
                            notIssuedByHand,
                        }),
                    (data) => data?.id,
                )
            }
        />
    )
}
